import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../utils/pricing';

// 紺帯番号バンド(home/page.tsx NavyBand と同一文法・移植)。帯=区切り(#31)／帯直後に同一文言見出しを反復しない(#33)。
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// ===== バトラー詳細 全面最新化（Implementer A スコープ） =====
// 土台: about/overview + home。カタログ: Hero=BB-02 / H2-02系 / SEC-N02,N04,N07,N09翻案。
// 事実正典（創作禁止）: 現行 working tree services/butler（特徴・シーン・1日の流れ・料金）+ hub料金表
//   + mng-thebutler/00_事業説明.md L76(バトラー=8時間専属)。料金データ変更禁止。

export default function ButlerPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事サービス', href: '/butler-services/' },
    { label: 'バトラー' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { en: 'Authentic', ja: '本格的な執事体験', d: '短時間のサービスでは味わえない、本物の執事との一日を。執事が常に傍らにいることで、必要な時にすぐサポートを受けられる安心感と、洗練された空間での優雅な時間をお過ごしいただけます。' },
    { en: 'Customized', ja: '完全カスタマイズ', d: '一日のスケジュールや生活スタイルに完全に合わせた設計が可能です。ビジネスサポート中心、家事サポート中心、来客対応中心など、ご要望に応じて柔軟に対応します。' },
    { en: 'For VIP', ja: 'VIP・富裕層向け対応', d: '高度なプライバシー配慮、機密情報の取り扱い、VIPゲストへの対応など、富裕層のお客様に必要とされる専門的なスキルを持つ執事が対応します。' },
    { en: 'Continuity', ja: '継続で深まる関係', d: '定期的にご利用いただくことで、執事がお客様の好みや習慣を深く理解し、よりパーソナライズされた質の高いサービスを提供できるようになります。' },
  ];

  const scenes = [
    { t: '重要なビジネスデー', d: '大切な商談や会議、プレゼン準備の日に。執事が朝食準備、身支度サポート、資料整理、昼食手配、来客対応などを担当し、お客様はビジネスに専念できます。' },
    { t: '大切なゲストの接待', d: 'VIPゲストをお迎えする際、執事が会場準備、料理の手配とサーブ、ドリンクサービス、会話の円滑化、片付けまでを一貫して担当。格式高い接待を実現します。' },
    { t: '特別なイベント・パーティー', d: '誕生日祝い、結婚記念日、企業イベントなど一日がかりの催しで、朝の準備から夜の片付けまで全面サポート。細かな調整も経験豊富な執事が的確に対応します。' },
    { t: '療養時・多忙な一日', d: '体調が優れない時や回復期に、食事準備・服薬管理・通院付き添い・家事をサポート。予定が詰まった日には生活面をすべてバックアップし、時間に追われず過ごせます。' },
  ];

  const schedule = [
    { t: 'ビジネスサポート中心の一日', rows: [['8:00', '出勤準備サポート、朝食準備'], ['10:00', '来客対応、ドリンクサービス'], ['12:00', '昼食準備・片付け'], ['13:00', '会議サポート、メール整理補助'], ['15:00', '買い物代行、夕食準備開始'], ['18:00', '後片付け、翌日の準備']] },
    { t: 'ホームパーティーの一日', rows: [['10:00', '会場準備、買い出し'], ['11:00', '料理準備、テーブルセッティング'], ['13:00', 'ゲスト受付開始'], ['14:00', '料理・ドリンクサーブ'], ['17:00', 'デザート・コーヒーサービス'], ['19:00', '片付け・完全撤収']] },
  ];

  const serviceGroups = [
    { t: '食事関連', d: '朝食・昼食・夕食の準備と配膳、食材の買い出し、食器の洗浄、テーブルセッティング、栄養バランスを考慮した献立提案' },
    { t: '家事全般', d: '清掃（リビング・寝室・浴室など）、洗濯・アイロンがけ、整理整頓、ベッドメイキング、観葉植物の水やり' },
    { t: 'ビジネスサポート', d: '資料整理、来客対応、会議室準備、ドリンクサービス、郵便物整理、スケジュール管理補助' },
    { t: 'パーソナルサポート', d: '買い物代行、クリーニング預け・受取、通院付き添い、ペットケア、子どもの送迎サポート' },
    { t: 'イベント・接待', d: '会場設営、料理サーブ、ゲスト対応、進行管理、片付け' },
    { t: 'その他', d: '執事の専門知識を活かした各種アドバイス、冠婚葬祭のマナー相談、贈答品の選定サポート' },
  ];

  const faqs = [
    ['3時間のフットマンとの違いは何ですか？', '8時間というまとまった時間により、より包括的で深いサービスが可能です。お客様のペースや習慣を把握した、一日を通した柔軟な対応ができます。'],
    ['時間帯は指定できますか？', '8:00〜21:00の間で、ご希望の8時間を自由に設定いただけます。'],
    ['毎週同じ曜日に来てもらえますか？', 'はい、定期契約も承っております。継続利用により、よりパーソナライズされた質の高いサービスを提供できます。'],
    ['複数の執事に来てもらえますか？', 'はい可能です。大規模なイベント等では複数名体制も承ります。'],
    ['調理もお願いできますか？', 'はい、基本的な家庭料理からおもてなし料理まで対応可能です。特別な料理は事前にご相談ください。'],
  ];

  const related = [
    { name: 'Footman', ja: 'フットマン', cad: '3時間からのスポット利用', price: yenInc(jbaDispatchExTax.footmanBase), unit: '円〜（税込）', href: '/services/footman/' },
    { name: 'Second Steward', ja: 'セカンドスチュワード', cad: '1ヶ月20日間の継続利用', price: yenInc(jbaDispatchExTax.secondStewardMonthly), unit: '円〜（税込）', href: '/services/second-steward/' },
    { name: 'House Steward', ja: 'ハウススチュワード', cad: '24時間・3人交代の常駐型', price: yenInc(jbaDispatchExTax.houseStewardMonthly), unit: '円（税込）', href: '/services/house-steward/' },
  ];

  return (
    <Layout>
      <title>バトラー｜1日8時間の専属執事サービスと料金 | 全日本執事協会 THE BUTLER</title>
      <meta name="description" content={`THE BUTLER のバトラーは、1日8時間、専属執事による本格的なおもてなしをご体験いただけるプラン。朝の準備から夕の片付けまで一日を支えます。料金${yenInc(jbaDispatchExTax.butlerBase)}円（税込・8時間）から、執事指名は無料です。`} />
      <link rel="canonical" href="https://butler-association.jp/services/butler" />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-p-041-svc-butler.webp" alt="バトラーサービス。専属執事が一日を通して暮らしを支える様子" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[700px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Plan Ⅱ — Butler</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(27px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">一日8時間、</span>
              <span className="block">専属の執事とともに</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              バトラーは、1日8時間というまとまった時間を専属執事が支える、当協会の中核プランです。朝の準備から夕方のサポートまで、経験豊富な執事が一日を通してお客様の生活を支えます。8時間だからこそ、執事はお客様のペースや習慣を把握し、より細やかで先を読んだおもてなしをお届けできます。
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <NavyBand no="I" ja="このサービスについて" en="Overview" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S1 特徴 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              まとまった時間が生む、<span className="text-[#a38e65]">上質な一日</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>バトラーは「本格的な執事サービスを体験したい」「一日中サポートが必要な特別な日がある」というお客様のために設計された中核プランです。8時間という十分な時間を確保することで、執事本来の価値である先を読んだ気配り、状況に応じた柔軟な対応、品格ある立ち居振る舞いを存分にご体験いただけます。</p>
              <p>全日本執事協会が130年以上の歴史の中で培ってきた執事教育の真髄が、このバトラーに凝縮されています。対応する執事は、厳格な認定試験に合格し実務経験を積んだプロフェッショナルのみ。単なる作業の代行ではなく、生活スタイルや好みを理解し最適なサービスを提供する能力を持つ人材です。</p>
              <p>8時間というまとまった時間だからこそ、執事はお客様のペースや習慣を把握し、より細やかな配慮ができます。朝の目覚めから朝食の準備、日中の様々なサポート、夕食の準備と片付けまで、一日の流れに沿った包括的なサービスが可能です。</p>
            </div>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8 mt-10">
              {features.map((f) => (
                <div key={f.ja} className="border-t border-[#e2ddd2] pt-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5">{f.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="II" ja="ご利用シーン" en="Scenes" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S2 シーン（画像split） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              バトラーが最も価値を発揮する場面
            </h2>
            <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {scenes.map((s) => (
                  <div key={s.t} className="border-l-2 border-[#a38e65]/40 pl-4">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                  </div>
                ))}
              </div>
              <img src="/images/gakken/gk-18.jpg" alt="執事が晩餐の食卓で皿を整え丁寧なバトラーサービスを準備する実写" className="w-full h-64 lg:h-[360px] object-cover border border-[#a38e65]/30 order-first lg:order-none" />
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="III" ja="執事との一日" en="A Day With Butler" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S3 一日の流れ（デュアルタイムライン）= 新バリエーション
               由来注記: overview S5 縦タイムラインを、2つの利用パターンを並置する時刻軸カードへ翻案。トップに時刻軸並置型は無いため新造。 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              標準的なスケジュール例
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              以下は一例です。8:00〜21:00の間でご希望の8時間を自由に設定でき、内容もご要望に合わせて自由にカスタマイズいただけます。
            </p>
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {schedule.map((sc) => (
                <div key={sc.t} className="bg-[#f7f5f0] p-7 sm:p-8">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-5">{sc.t}</h3>
                  <ol className="space-y-3.5">
                    {sc.rows.map(([time, act]) => (
                      <li key={time} className="flex gap-5 font-['Noto_Serif_JP',serif] text-[15px] leading-[1.7]">
                        <span className="w-14 shrink-0 text-[#a38e65] font-['Playfair_Display',serif]">{time}</span>
                        <span className="text-[#333]">{act}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="IV" ja="提供サービス" en="What We Do" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S4 提供サービス内容 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              8時間で提供できる包括的なサービス
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
              {serviceGroups.map((g) => (
                <div key={g.t} className="border-t border-[#e2ddd2] pt-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">{g.t}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{g.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="V" ja="料金" en="Pricing" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S5 料金 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              明確でわかりやすい料金
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] p-8 text-center">
                <div className="font-['Noto_Serif_JP',serif] text-[42px] text-[#f8f6f0] leading-none">{yenInc(jbaDispatchExTax.butlerBase)}<span className="text-lg text-[#d7b779] ml-1">円</span></div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] mt-3">税込・8時間</p>
              </div>
              <div>
                <dl className="border-t-2 border-[#1a2942]">
                  {[
                    ['基本料金', `8時間：${yenInc(jbaDispatchExTax.butlerBase)}円（税込）`],
                    ['利用時間帯', '8:00〜21:00の間で8時間'],
                    ['延長料金', `${yenInc(jbaDispatchExTax.butlerExtension)}円/時間（税込）`],
                    ['執事指名', '無料'],
                    ['交通費', '実費精算'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em]">{k}</dt>
                      <dd className="font-['Noto_Serif_JP',serif] text-[17px] text-[#1a2942] text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mt-6">
                  <span className="text-[#8f7a56]">お支払い：</span>銀行振込、クレジットカード。キャンセルは7日前まで無料／3〜6日前30%／前日〜当日100%。
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VI" ja="よくあるご質問" en="FAQ" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S6 FAQ */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくあるご質問
            </h2>
            <div className="max-w-3xl divide-y divide-[#e2ddd2] border-t border-[#e2ddd2]">
              {faqs.map(([q, a]) => (
                <div key={q} className="py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">Q. {q}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VII" ja="その他のプラン" en="Other Plans" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S7 関連サービス */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              他のプランと比べる
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              単発でお試しならフットマン、継続して支えてほしいならセカンドスチュワード、邸宅全体を任せるならハウススチュワードへ。
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <a key={r.name} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] transition-colors duration-300 p-6">
                  <span className="block font-['Playfair_Display',serif] text-[13px] tracking-[0.1em] text-[#8f7a56] mb-1">{r.name}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 group-hover:text-[#a38e65] transition-colors">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mb-4">{r.cad}</p>
                  <div className="font-['Noto_Serif_JP',serif] text-[22px] text-[#1a2942]">{r.price}<span className="text-sm text-[#8f7a56] ml-1">{r.unit}</span></div>
                </a>
              ))}
            </div>
            <div className="mt-8"><a href="/butler-services/" className={BTN08_ON_LIGHT}>執事サービス一覧へ戻る</a></div>
          </section>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S8 CTA */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <h2 className="jba-display font-medium text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.5] mb-4">バトラーで上質な一日を</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                お電話またはフォームでご希望をお聞かせください。専門スタッフが詳しくご要望を伺い、最適なサービスプランと料金をご提案します。
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせフォームへ</a>
                <span className="font-['Noto_Serif_JP',serif] text-[#e7e2d8] text-[15px]">お電話：03-6824-4086（平日10:00-18:00）</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
